import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Building2, Users, ShieldCheck, BookOpen, Check, X, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function OnboardingWizard() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [emails, setEmails] = useState("");
  const [expiryDays, setExpiryDays] = useState(7);

  const isOrgAdmin = (user?.is_primary_admin === true || user?.is_org_admin === true) && user?.account_type === "organization";

  useEffect(() => {
    if (isOrgAdmin && !user?.onboarding_completed) {
      setOpen(true);
      api.get("/org/settings").then((r) => {
        setExpiryDays(r.data.invite_expiry_days || 7);
      }).catch(() => {});
    }
  }, [isOrgAdmin, user?.onboarding_completed]);

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/account/complete-onboarding");
      await refresh();
    } catch { /* non-blocking */ }
    setBusy(false);
    setOpen(false);
  };

  const sendInvites = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { setStep(2); return; }
    setBusy(true);
    try {
      const res = await api.post("/org/members/bulk", { emails: list });
      if (res.data.created_count > 0) toast.success(t("onboarding.toastInvited", { count: res.data.created_count }));
      if (res.data.skipped_count > 0) toast.info(t("onboarding.toastSkipped", { count: res.data.skipped_count }));
      setEmails("");
      setStep(2);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const savePolicy = async () => {
    setBusy(true);
    try {
      await api.put("/org/invite-expiry", { days: expiryDays });
      toast.success(t("onboarding.toastPolicySaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
    setStep(3);
  };

  const STEPS = [
    { icon: Building2, label: "Welcome" },
    { icon: Users, label: "Invite team" },
    { icon: ShieldCheck, label: "Set policy" },
    { icon: Sparkles, label: "Done" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden gap-0 [&>button]:hidden" data-testid="onboarding-wizard">
        <DialogTitle className="sr-only">{t("onboarding.srTitle")}</DialogTitle>
        <DialogDescription className="sr-only">{t("onboarding.srDesc")}</DialogDescription>
        {/* Header / progress */}
        <div className="bg-[#F7F7F8] px-6 py-5 text-[#202123] border-b border-[#E5E5E5]">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-wide text-[#6E6E80]">{t("onboarding.getStartedStep", { n: step + 1, total: STEPS.length })}</p>
            <button onClick={finish} className="text-[#6E6E80] hover:text-[#202123] transition-colors" data-testid="onboarding-close" aria-label={t("onboarding.skipSetup")}><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#202123]" : "bg-[#E5E5E5]"}`} />
            ))}
          </div>
        </div>

        <div className="px-6 py-6">
          {step === 0 && (
            <div data-testid="onboarding-step-welcome">
              <div className="h-12 w-12 rounded-xl bg-[#ECECEC] flex items-center justify-center mb-4"><Building2 className="h-6 w-6 text-[#202123]" /></div>
              <h2 className="font-display text-xl font-semibold text-navy">{t("onboarding.welcomeTitle")}</h2>
              <p className="text-sm text-slatebody mt-2 leading-relaxed">
                {t("onboarding.welcomeBody")}
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="ghost" onClick={finish} data-testid="onboarding-skip-all">{t("onboarding.skipForNow")}</Button>
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={() => setStep(1)} data-testid="onboarding-start">{t("onboarding.letsGo")}</Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-invite">
              <div className="h-12 w-12 rounded-xl bg-[#ECECEC] flex items-center justify-center mb-4"><Users className="h-6 w-6 text-[#202123]" /></div>
              <h2 className="font-display text-xl font-semibold text-navy">{t("onboarding.inviteTitle")}</h2>
              <p className="text-sm text-slatebody mt-1.5 mb-4">{t("onboarding.inviteBody")}</p>
              <Textarea rows={4} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="jane@company.com, john@company.com" data-testid="onboarding-emails" />
              <p className="text-xs text-slatebody mt-2">{t("onboarding.inviteAsMemberNote")}</p>
              <div className="mt-6 flex justify-between">
                <Button variant="ghost" onClick={() => setStep(2)} data-testid="onboarding-skip-invite">{t("onboarding.skip")}</Button>
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={busy} onClick={sendInvites} data-testid="onboarding-send-invites">{busy ? t("onboarding.sending") : (emails.trim() ? t("onboarding.sendInvitesContinue") : t("onboarding.continue"))}</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-policy">
              <div className="h-12 w-12 rounded-xl bg-[#ECECEC] flex items-center justify-center mb-4"><ShieldCheck className="h-6 w-6 text-[#202123]" /></div>
              <h2 className="font-display text-xl font-semibold text-navy">{t("onboarding.policyTitle")}</h2>
              <p className="text-sm text-slatebody mt-1.5 mb-4">{t("onboarding.policyBody")}</p>

              <div className="mb-2">
                <p className="text-sm font-medium text-navy mb-1.5">{t("onboarding.invitesExpireAfter")}</p>
                <Select value={String(expiryDays)} onValueChange={(v) => setExpiryDays(Number(v))}>
                  <SelectTrigger className="h-9 w-40" data-testid="onboarding-expiry"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{[3, 7, 14].map((d) => <SelectItem key={d} value={String(d)}>{t("onboarding.daysOption", { count: d })}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="mt-6 flex justify-between">
                <Button variant="ghost" onClick={() => setStep(3)} data-testid="onboarding-skip-policy">{t("onboarding.skip")}</Button>
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={busy} onClick={savePolicy} data-testid="onboarding-save-policy">{busy ? t("onboarding.saving") : t("onboarding.saveContinue")}</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-done">
              <div className="h-12 w-12 rounded-xl bg-[#ECECEC] flex items-center justify-center mb-4"><Check className="h-6 w-6 text-[#202123]" /></div>
              <h2 className="font-display text-xl font-semibold text-navy">{t("onboarding.doneTitle")}</h2>
              <p className="text-sm text-slatebody mt-2 leading-relaxed">{t("onboarding.doneBody")}</p>

              <button
                onClick={() => { navigate("/app/context"); finish(); }}
                data-testid="onboarding-context-nudge"
                className="mt-4 w-full text-left rounded-xl border border-slate-200 hover:border-[#C5C5D0] hover:bg-[#E0E0E0]/5 transition-colors p-4 flex items-start gap-3">
                <BookOpen className="h-5 w-5 text-[#202123] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-navy">{t("onboarding.addContextTitle")} <span className="text-[11px] font-normal text-slatebody">· {t("onboarding.optional")}</span></p>
                  <p className="text-xs text-slatebody mt-0.5">{t("onboarding.addContextBody")}</p>
                </div>
              </button>

              <div className="mt-6 flex justify-end">
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={busy} onClick={finish} data-testid="onboarding-finish">{t("onboarding.finish")}</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
